import React, { useState, useEffect } from 'react';
import { X, Spinner, Globe, ShieldCheck } from '@phosphor-icons/react';
import { domainsApi } from '../services/api';

const DomainDetailSheet = ({ domain, onClose }) => {
  const [activeTab, setActiveTab] = useState('whois');
  const [whoisData, setWhoisData] = useState(null);
  const [dnsRecords, setDnsRecords] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (activeTab === 'whois' && !whoisData) {
      loadWhoisData();
    } else if (activeTab === 'dns' && !dnsRecords) {
      loadDnsRecords();
    }
  }, [activeTab]);

  const loadWhoisData = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await domainsApi.getWhois(domain.id);
      setWhoisData(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al cargar información WHOIS');
    } finally {
      setLoading(false);
    }
  };

  const loadDnsRecords = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await domainsApi.getDnsRecords(domain.id);
      setDnsRecords(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al cargar registros DNS');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end md:items-center justify-center md:justify-end z-50">
      <div className="bg-white w-full md:w-[600px] h-[90vh] md:h-full md:max-h-screen overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-white border-b border-zinc-200 p-6 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
              {domain.dominio}
            </h2>
            <p className="text-sm text-zinc-600 mt-1">{domain.empresa}</p>
          </div>
          <button
            data-testid="sheet-close-button"
            onClick={onClose}
            className="text-zinc-500 hover:text-zinc-900"
          >
            <X size={24} />
          </button>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div>
              <p className="text-xs uppercase tracking-widest text-zinc-500 font-semibold mb-1">Ambiente</p>
              <p className="font-semibold">{domain.ambiente}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest text-zinc-500 font-semibold mb-1">Registrador</p>
              <p className="font-semibold">{domain.registrador_name}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest text-zinc-500 font-semibold mb-1">DNS Provider</p>
              <p className="font-semibold">{domain.dns_name}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest text-zinc-500 font-semibold mb-1">Vencimiento</p>
              <p className="font-semibold">{domain.vencimiento}</p>
            </div>
          </div>

          {domain.notas && (
            <div className="mb-6 p-4 bg-zinc-50 border border-zinc-200 rounded">
              <p className="text-xs uppercase tracking-widest text-zinc-500 font-semibold mb-2">Notas</p>
              <p className="text-sm">{domain.notas}</p>
            </div>
          )}

          <div className="border-b border-zinc-200 mb-6">
            <div className="flex gap-4">
              <button
                data-testid="tab-whois"
                onClick={() => setActiveTab('whois')}
                className={`pb-3 px-2 font-semibold border-b-2 transition-colors ${
                  activeTab === 'whois'
                    ? 'border-[#002FA7] text-[#002FA7]'
                    : 'border-transparent text-zinc-600 hover:text-zinc-900'
                }`}
              >
                <Globe size={20} className="inline mr-2" />
                Información WHOIS
              </button>
              <button
                data-testid="tab-dns"
                onClick={() => setActiveTab('dns')}
                className={`pb-3 px-2 font-semibold border-b-2 transition-colors ${
                  activeTab === 'dns'
                    ? 'border-[#002FA7] text-[#002FA7]'
                    : 'border-transparent text-zinc-600 hover:text-zinc-900'
                }`}
              >
                <ShieldCheck size={20} className="inline mr-2" />
                Registros DNS
              </button>
            </div>
          </div>

          {loading && (
            <div className="flex items-center justify-center py-12">
              <Spinner className="animate-spin text-[#002FA7]" size={32} />
            </div>
          )}

          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
              {error}
            </div>
          )}

          {!loading && !error && activeTab === 'whois' && whoisData && (
            <div className="space-y-4">
              {Object.entries(whoisData).map(([key, value]) => (
                <div key={key} className="border-b border-zinc-100 pb-3">
                  <p className="text-xs uppercase tracking-widest text-zinc-500 font-semibold mb-1">
                    {key.replace(/_/g, ' ')}
                  </p>
                  <p className="text-sm font-mono">
                    {Array.isArray(value) ? value.join(', ') : String(value || 'N/A')}
                  </p>
                </div>
              ))}
            </div>
          )}

          {!loading && !error && activeTab === 'dns' && dnsRecords && (
            <div className="space-y-4">
              {Object.entries(dnsRecords).map(([recordType, records]) => (
                <div key={recordType} className="border border-zinc-200 rounded-lg p-4">
                  <h3 className="text-sm font-bold uppercase tracking-widest text-zinc-700 mb-3">
                    {recordType} Records
                  </h3>
                  {records.length === 0 ? (
                    <p className="text-sm text-zinc-500">No hay registros</p>
                  ) : (
                    <ul className="space-y-2">
                      {records.map((record, idx) => (
                        <li key={idx} className="text-sm font-mono bg-zinc-50 p-2 rounded">
                          {record}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DomainDetailSheet;